import * as React from "react";
import { NewMessage } from "source/components/framework/Message/NewMessage";
import { Base } from "ui/Scripts/source/framework/base";
import { ConfirmationDialogResult, FileType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../../framework/appUtils";
import { SaveDataObj } from "../../../../framework/saveData";
import { EventEditItemMessage, IEventEditItemMessage } from "ui/Scripts/source/models/event/eventEditItemMessage";
import * as eventService from "../../../../models/services/eventService";
import { StoreUtils } from "../../../../models/store/storeUtils";
import { SaveAttachmentBase64 } from "ui/Scripts/source/models/common/saveAttachmentBase64";
import { FileAttachment } from "ui/Scripts/source/models/common/fileAttachment";

export interface IEventEditDialogMessageListNewProp {
    classes?: string;
    maxEventMessageAttachmentSize: number;
    isReadOnly: boolean;
    companyId: string;
    eventId: string;
    onEventMessageSaved: (item: IEventEditItemMessage) => void;
}

interface IEventEditDialogMessageListNewState {
    content: string;
    attachments: FileAttachment[];
    sending: boolean;
}

export class EventEditDialogMessageListNew extends React.Component<IEventEditDialogMessageListNewProp, IEventEditDialogMessageListNewState> {
    constructor(props: IEventEditDialogMessageListNewProp) {
        super(props);
        this.state = {
            content: "",
            attachments: [],
            sending: false
        };
    }

    getSendingDisabled = (): boolean => {
        const state = this.state;
        return state.sending || !state.content && state.attachments.length < 1;
    };

    getSaveDataFromState = async (props: IEventEditDialogMessageListNewProp, state: IEventEditDialogMessageListNewState): Promise<SaveDataObj> => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("eventId", props.eventId);
        result.append("content", state.content);
        result.append("attachments", await SaveAttachmentBase64.fromFileAttachments(state.attachments.filter(i => !!i.file)));
        return result;
    };

    updateSavedMessageToList = (id: string) => {
        const props = this.props;
        const state = this.state;
        const newMessage = new EventEditItemMessage();
        newMessage.id = id;
        newMessage.content = state.content;
        newMessage.myMessage = true;
        newMessage.modifyUser = StoreUtils.getNowModifyUser(Base.getNowTicks());
        newMessage.attachments = state.attachments.slice(0);
        props.onEventMessageSaved(newMessage);
    };

    saveEventMessage = async () => {
        const props = this.props;
        const state = this.state;
        if (this.getSendingDisabled()) return;
        const confirmation = await AppUtils.showConfirmationDialog(Translations.EventMessageConfirmation);
        if (confirmation !== ConfirmationDialogResult.Yes) return;
        this.setState({ sending: true });
        try {
            const saveData = await this.getSaveDataFromState(props, state);
            //Call server
            const result = await AppUtils.callService(() => eventService.eventEditAddMessage(saveData.obj), false, true);
            if (!result) return null;
            this.updateSavedMessageToList(result.id);
            this.setState({
                content: "",
                attachments: []
            });
        } finally {
            this.setState({ sending: false });
        }
    };

    //# region General
    handleChange = (event) => {
        this.setState({ content: event.target.value });
    };
    // #endregion General

    // #region Attachments
    handleAddAttachment = async (fileList: FileList) => {
        const state = this.state;
        const attachments = await FileAttachment.addFileListToFileAttachments(state.attachments, fileList, 0);
        if (attachments.length < 1) return;
        for (const attachment of attachments) {
            if (!AppUtils.validateFileAttachment(attachment, Translations.MaximumSizeOfInstantMessageAttachmentIsParameterMB, this.props.maxEventMessageAttachmentSize)) return;
        }
        if (!AppUtils.validateFileAttachments(attachments)) return;
        this.setState({
            attachments
        });
    };

    handleAddAttachments = (newAttachments: FileAttachment[]) => {
        if (!newAttachments || newAttachments.length < 1) return;
        const attachments = this.state.attachments.slice(0);
        for (const newAttachment of newAttachments) {
            if (!newAttachment) return;
            attachments.push(newAttachment);
        }
        if (attachments.length < 1 || !AppUtils.validateFileAttachments(attachments)) return;
        this.setState({
            attachments
        });
    };

    handleRemoveAttachment = async (id: string) => {
        const state = this.state;
        if (!id) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveAttachmentParameter, state.attachments.find(x => x.id === id)?.name), false) === ConfirmationDialogResult.No) return;
        const attachments = state.attachments.filter(i => i.id !== id);
        this.setState({
            attachments
        });
    };
    // #endregion Attachments
        
    render() {
        const props = this.props;
        const state = this.state;
        const imageAttachments = state.attachments.filter(i => i.type === FileType.Image);
        const fileAttachments = state.attachments.filter(i => i.type !== FileType.Image);
        return (
            <NewMessage
                message={state.content}
                fileAttachments={fileAttachments}
                imageAttachments={imageAttachments}
                disabled={props.isReadOnly}
                sendDisabled={props.isReadOnly || this.getSendingDisabled()}
                sendButtonTooltip={Translations.SaveComment}
                onChange={(e) => this.handleChange(e)}
                onAddAttachment={this.handleAddAttachment}
                onRemoveAttachment={this.handleRemoveAttachment}
                onSend={this.saveEventMessage}
            />
        );
    }
}